import { useState, useEffect } from 'react';
import Spinner from '@cloudscape-design/components/spinner';
import Box from '@cloudscape-design/components/box';
import Icon from '@cloudscape-design/components/icon';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Badge from '@cloudscape-design/components/badge';
import { api, type MessageEnvelope, type MessageDetail } from '../api';
import { useSearch } from '../search/SearchContext';
import { useTenant } from '../tenant/TenantContext';
import ThreadView from '../components/ThreadView';
import TicketPanel from '../components/TicketPanel';
import { mockTickets, type Email } from '../data/mockEmails';
import { format, isToday, isYesterday, parseISO } from 'date-fns';

/** Generate a consistent color from a string (for avatar backgrounds) */
function stringToColor(str: string): string {
  const colors = ['#0972d3', '#037f0c', '#5f6b7a', '#8b5cf6', '#d63384', '#e07941', '#2ea597', '#7d8998'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

/** Get initials from a display name or email */
function getInitials(from: string): string {
  // "John Smith <john@example.com>" -> "JS"
  const name = from.replace(/<.*>/, '').trim();
  if (!name) return '?';
  const parts = name.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name[0].toUpperCase();
}

/** Get display name from the "from" field */
function getDisplayName(from: string): string {
  const match = from.match(/^(.+?)\s*<.*>$/);
  return match ? match[1].trim() : from.split('@')[0];
}

/** Format time like Outlook: "8:25 PM" for today, "Yesterday", or "Aug 12" */
function formatMessageTime(isoString: string): string {
  if (!isoString) return '';
  const date = parseISO(isoString);
  if (isToday(date)) return format(date, 'h:mm a');
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMM d');
}

/** Group messages by date header like Outlook */
function groupByDate(messages: MessageEnvelope[]): { label: string; messages: MessageEnvelope[] }[] {
  const groups: Map<string, MessageEnvelope[]> = new Map();
  for (const msg of messages) {
    const date = parseISO(msg.receivedAt);
    let label: string;
    if (isToday(date)) label = 'Today';
    else if (isYesterday(date)) label = 'Yesterday';
    else label = format(date, 'EEEE, MMMM d');
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(msg);
  }
  return Array.from(groups.entries()).map(([label, messages]) => ({ label, messages }));
}

export default function InboxView() {
  const [emails, setEmails] = useState<MessageEnvelope[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<MessageEnvelope | null>(null);
  const [emailDetail, setEmailDetail] = useState<MessageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'read' | 'unread'>('all');
  const [showTicketPanel, setShowTicketPanel] = useState(true);
  const { activeQuery, clearSearch } = useSearch();
  const { tenant } = useTenant();

  // Switching mailbox invalidates the open message — clear it so the reading
  // pane never shows a message belonging to the previous tenant.
  useEffect(() => {
    setSelectedEmail(null);
    setEmailDetail(null);
  }, [tenant?.tenantId]);

  useEffect(() => {
    loadEmails();
  }, [filter, activeQuery, tenant?.tenantId]);

  // WM-14: Poll for new messages every 10 seconds.
  // Polling is paused while a search is active so results don't get clobbered.
  useEffect(() => {
    if (activeQuery) return;
    const interval = setInterval(async () => {
      try {
        const status = filter === 'all' ? undefined : filter;
        const res = await api.listMessages({ limit: 25, status });
        setEmails((prev) => {
          const prevIds = new Set(prev.map((e) => e.messageId));
          const hasNew = res.items.some((e) => !prevIds.has(e.messageId));
          if (hasNew || res.items.length !== prev.length) {
            return res.items;
          }
          return prev;
        });
      } catch {
        // Silently ignore polling errors
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [filter, activeQuery, tenant?.tenantId]);

  async function loadEmails() {
    setLoading(true);
    try {
      if (activeQuery) {
        // Inline search — filter the inbox by the search API results
        const res = await api.searchMessages(activeQuery);
        const status = filter === 'all' ? undefined : filter;
        setEmails(status ? res.items.filter((e) => e.readStatus === status) : res.items);
      } else {
        const status = filter === 'all' ? undefined : filter;
        const res = await api.listMessages({ limit: 25, status });
        setEmails(res.items);
      }
    } catch (err) {
      console.error('Failed to load emails', err);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleSelectEmail(email: MessageEnvelope) {
    setSelectedEmail(email);
    setDetailLoading(true);
    try {
      const detail = await api.getMessage(email.messageId);
      setEmailDetail(detail);
      if (email.readStatus !== 'read') {
        await api.updateMessage(email.messageId, { readStatus: 'read' }).catch(() => {});
        setEmails((prev) => prev.map((e) => (e.messageId === email.messageId ? { ...e, readStatus: 'read' } : e)));
      }
    } catch (err) {
      console.error('Failed to load email detail', err);
    } finally {
      setDetailLoading(false);
    }
  }

  const unreadCount = emails.filter((e) => e.readStatus !== 'read').length;
  const dateGroups = groupByDate(emails);

  // Ticket Tracker plugin: the linked ticket is resolved by threadId.
  // Ticket data is still mock — the TicketLinks backend contract is not built
  // yet (see webmail design.md "Ticket Tracker Plugin").
  const linkedTicket = selectedEmail ? mockTickets.find((t) => t.threadId === selectedEmail.threadId) : undefined;

  // TicketPanel takes the richer `Email` shape; adapt the envelope we have.
  const ticketEmail: Email | null = selectedEmail
    ? {
        messageId: selectedEmail.messageId,
        threadId: selectedEmail.threadId || '',
        from: selectedEmail.from,
        fromName: getDisplayName(selectedEmail.from),
        to: [selectedEmail.to],
        subject: selectedEmail.subject,
        snippet: selectedEmail.snippet || '',
        body: emailDetail?.body || '',
        timestamp: selectedEmail.receivedAt,
        readStatus: selectedEmail.readStatus === 'read',
        labels: Array.isArray(selectedEmail.labels) ? selectedEmail.labels : [],
        attachments: [],
      }
    : null;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 56px)', background: '#fafafa' }}>
      {/* ─── Message List (Outlook middle column) ─── */}
      <div
        style={{
          width: '420px',
          minWidth: '360px',
          borderRight: '1px solid #d5dbdb',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            padding: '8px 16px',
            borderBottom: '1px solid #e9ebed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
          }}
        >
          <SpaceBetween direction="horizontal" size="xs">
            <ButtonDropdown
              items={[
                { id: 'all', text: 'All' },
                { id: 'unread', text: 'Unread' },
                { id: 'read', text: 'Read' },
              ]}
              onItemClick={({ detail }) => setFilter(detail.id as 'all' | 'read' | 'unread')}
              variant="normal"
            >
              {filter === 'all' ? 'All' : filter === 'unread' ? 'Unread' : 'Read'}
            </ButtonDropdown>
            {unreadCount > 0 && (
              <span style={{ fontSize: '12px', color: '#5f6b7a' }}>{unreadCount} unread</span>
            )}
          </SpaceBetween>
          <SpaceBetween direction="horizontal" size="xxs">
            <Button iconName="refresh" variant="icon" ariaLabel="Refresh" onClick={loadEmails} />
            <ButtonDropdown
              items={[
                { id: 'date', text: 'By Date' },
                { id: 'sender', text: 'By Sender' },
              ]}
              variant="icon"
              ariaLabel="Sort"
            >
              {''}
            </ButtonDropdown>
          </SpaceBetween>
        </div>

        {/* Active search indicator */}
        {activeQuery && (
          <div
            style={{
              padding: '8px 16px',
              background: '#f0f7ff',
              borderBottom: '1px solid #b8d8f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              fontSize: '13px',
            }}
          >
            <span style={{ color: '#0972d3' }}>
              {emails.length} result{emails.length === 1 ? '' : 's'} for &ldquo;{activeQuery}&rdquo;
            </span>
            <Button variant="inline-link" onClick={clearSearch}>
              Clear
            </Button>
          </div>
        )}

        {/* Message list with date grouping */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <Box textAlign="center" padding="l">
              <Spinner size="large" />
            </Box>
          ) : emails.length === 0 ? (
            <Box textAlign="center" padding="l" color="text-body-secondary">
              <Icon name="envelope" size="big" />
              <Box margin={{ top: 's' }}>{activeQuery ? 'No matching emails' : 'No emails yet'}</Box>
            </Box>
          ) : (
            dateGroups.map((group) => (
              <div key={group.label}>
                {/* Date group header */}
                <div
                  style={{
                    padding: '6px 16px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#5f6b7a',
                    background: '#f2f3f3',
                    borderBottom: '1px solid #e9ebed',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  {group.label}
                </div>

                {/* Messages in this group */}
                {group.messages.map((email) => {
                  const isSelected = selectedEmail?.messageId === email.messageId;
                  const isUnread = email.readStatus !== 'read';
                  return (
                    <div
                      key={email.messageId}
                      onClick={() => handleSelectEmail(email)}
                      style={{
                        display: 'flex',
                        padding: '10px 16px',
                        gap: '12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f2f3f3',
                        borderLeft: isSelected ? '3px solid #0972d3' : '3px solid transparent',
                        background: isSelected ? '#f0f8ff' : 'transparent',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.background = '#f7f8f8';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      {/* Avatar */}
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          backgroundColor: stringToColor(email.from),
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: '14px',
                          flexShrink: 0,
                          marginTop: '2px',
                        }}
                      >
                        {getInitials(email.from)}
                      </div>

                      {/* Message content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <span
                            style={{
                              fontWeight: isUnread ? 700 : 400,
                              fontSize: '14px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: '#000716',
                            }}
                          >
                            {getDisplayName(email.from)}
                          </span>
                          <span style={{ fontSize: '12px', color: '#5f6b7a', flexShrink: 0, marginLeft: '8px' }}>
                            {formatMessageTime(email.receivedAt)}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: isUnread ? 600 : 400,
                            color: isUnread ? '#0972d3' : '#000716',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            marginTop: '1px',
                          }}
                        >
                          {email.subject || '(no subject)'}
                        </div>
                        {email.snippet && (
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#5f6b7a',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              marginTop: '2px',
                            }}
                          >
                            {email.snippet}
                          </div>
                        )}
                      </div>

                      {/* Unread indicator dot */}
                      {isUnread && (
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#0972d3',
                            flexShrink: 0,
                            marginTop: '6px',
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ─── Reading Pane (Outlook right column) ─── */}
      <div style={{ flex: 1, overflowY: 'auto', background: '#fff' }}>
        {detailLoading ? (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        ) : emailDetail && selectedEmail ? (
          <div style={{ padding: '24px 32px', maxWidth: '900px' }}>
            <SpaceBetween size="l">
              {/* Subject + actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#000716' }}>
                    {selectedEmail.subject}
                  </h2>
                  <SpaceBetween direction="horizontal" size="xxs">
                    {(Array.isArray(selectedEmail.labels) ? selectedEmail.labels : [])
                      .filter(Boolean)
                      .map((label) => (
                        <Badge key={label}>{label}</Badge>
                      ))}
                  </SpaceBetween>
                </div>
                <SpaceBetween direction="horizontal" size="xxs">
                  <Button iconName="status-pending" variant="icon" ariaLabel="Mark unread"
                    onClick={async () => {
                      await api.updateMessage(selectedEmail.messageId, { readStatus: 'unread' }).catch(() => {});
                      setEmails((prev) =>
                        prev.map((e) => (e.messageId === selectedEmail.messageId ? { ...e, readStatus: 'unread' } : e)),
                      );
                    }}
                  />
                  <Button iconName="remove" variant="icon" ariaLabel="Delete" />
                </SpaceBetween>
              </div>

              {/* Sender card */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: '1px solid #e9ebed',
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: stringToColor(selectedEmail.from),
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '16px',
                    flexShrink: 0,
                  }}
                >
                  {getInitials(selectedEmail.from)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#000716' }}>{getDisplayName(selectedEmail.from)}</div>
                  <div style={{ fontSize: '12px', color: '#5f6b7a' }}>
                    To: {selectedEmail.to}
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#5f6b7a' }}>
                  {selectedEmail.receivedAt
                    ? format(parseISO(selectedEmail.receivedAt), "MMM d, yyyy 'at' h:mm a")
                    : ''}
                </div>
              </div>

              {/* Email body */}
              {(() => {
                const threadMessages = selectedEmail.threadId
                  ? emails
                      .filter((e) => e.threadId === selectedEmail.threadId)
                      .sort((a, b) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime())
                      .map((e) => ({
                        messageId: e.messageId,
                        from: e.from,
                        subject: e.subject,
                        receivedAt: e.receivedAt,
                        readStatus: e.readStatus,
                        body: e.messageId === selectedEmail.messageId ? emailDetail.body : null,
                        snippet: e.snippet,
                      }))
                  : [
                      {
                        messageId: selectedEmail.messageId,
                        from: selectedEmail.from,
                        subject: selectedEmail.subject,
                        receivedAt: selectedEmail.receivedAt,
                        readStatus: selectedEmail.readStatus,
                        body: emailDetail.body,
                        snippet: selectedEmail.snippet,
                      },
                    ];

                if (threadMessages.length === 1 && emailDetail.body) {
                  return (
                    <div
                      style={{ fontSize: '14px', lineHeight: 1.6, color: '#000716' }}
                      dangerouslySetInnerHTML={{ __html: emailDetail.bodyHtml || emailDetail.bodyText || '' }}
                    />
                  );
                }
                return <ThreadView messages={threadMessages} activeMessageId={selectedEmail.messageId} />;
              })()}
            </SpaceBetween>
          </div>
        ) : (
          /* Empty state — like Outlook's "Select an item to read" */
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#5f6b7a',
              gap: '12px',
            }}
          >
            <svg width="120" height="90" viewBox="0 0 120 90" fill="none">
              <rect x="10" y="20" width="100" height="60" rx="4" stroke="#d5dbdb" strokeWidth="2" fill="#f7f8f8" />
              <path d="M10 24 L60 55 L110 24" stroke="#d5dbdb" strokeWidth="2" fill="none" />
              <circle cx="95" cy="20" r="8" fill="#0972d3" />
            </svg>
            <div style={{ fontSize: '16px', fontWeight: 500 }}>Select an item to read</div>
            <div style={{ fontSize: '13px' }}>Nothing is selected</div>
          </div>
        )}
      </div>

      {/* ─── Ticket Tracker plugin panel (collapsible 3rd column) ─── */}
      {showTicketPanel && (
        <div
          style={{
            width: '320px',
            minWidth: '280px',
            borderLeft: '1px solid #d5dbdb',
            overflowY: 'auto',
            background: '#fff',
          }}
        >
          <TicketPanel email={ticketEmail} ticket={linkedTicket} />
        </div>
      )}

      {/* ─── Plugin rail ─── */}
      <div
        style={{
          width: '48px',
          borderLeft: '1px solid #d5dbdb',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '12px',
          gap: '8px',
          background: '#fff',
        }}
      >
        <Button
          iconName="ticket"
          variant={showTicketPanel ? 'primary' : 'icon'}
          ariaLabel={showTicketPanel ? 'Hide Ticket Tracker' : 'Show Ticket Tracker'}
          onClick={() => setShowTicketPanel((v) => !v)}
        />
      </div>
    </div>
  );
}
